import React from 'react'
import { createPortal } from 'react-dom'
import { BlackbookAlertPrompt } from '@mobi/component-library/Areas/Blackbook/BlackbookAlertPrompt'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { incrementQuickCounter } from '@mobi/utils/logging/quickCounter'
import { useBlackbookPushAlertMutation } from '@core/Areas/Blackbook/Hooks/useBlackbookPushAlertMutation'
import { useBlackbookSmsEmailAlertMutation } from '@core/Areas/Blackbook/Hooks/useBlackbookSmsEmailAlertMutation'
import { updateLastNotificationPrompt } from '@core/Areas/Blackbook/Store'
import { trackBlackbookAlertEvent } from '@core/Areas/Blackbook/analytics'
import { useAppDispatch } from '@core/Store/hooks'

type Props = {
  accountNumber: number
  showPushNotificationPrompt: boolean
  sendEmail?: boolean
}

export const BlackbookAlertPromptTray = ({
  accountNumber,
  showPushNotificationPrompt,
  sendEmail,
}: Props) => {
  const dispatch = useAppDispatch()

  const { mutateAsync: mutatePushAlertAsync } = useBlackbookPushAlertMutation({
    accountNumber,
  })

  const { mutateAsync: mutateSmsEmailAlertAsync } = useBlackbookSmsEmailAlertMutation()

  const onAlertMethodChosen = async (method: 'sms' | 'push') => {
    try {
      if (method === 'push') {
        // Disable SMS alerts, enable push alerts
        trackEventWithSource('push')
        const pushResult = await mutatePushAlertAsync(true)

        if (pushResult === undefined) {
          return
        }

        await mutateSmsEmailAlertAsync({
          SendEmail: sendEmail || false,
          SendSms: false,
        })

        dispatch(updateLastNotificationPrompt())
      } else if (method === 'sms') {
        // .. no-op we assume sms is already enabled if the user
        // is seeing this dialog
        trackEventWithSource('sms')
        dispatch(updateLastNotificationPrompt())
      }
    } catch (error) {
      addToast({
        title:
          (error as { message?: string })?.message ||
          'Failed to update alert method. Please try again.',
        id: 'blackbook-push-prompt-update-error',
        icon: 'error',
        timeoutMs: 1_500,
      })
    }
  }

  const onAlertPromptClosed = () => {
    trackEventWithSource('closed')
    dispatch(updateLastNotificationPrompt())
  }

  return createPortal(
    <Tray
      isOpen={showPushNotificationPrompt}
      dismissStatus='disallowed'
      shouldCoverFullscreen={true}
    >
      <BlackbookAlertPrompt
        onAlertMethodChosen={onAlertMethodChosen}
        onClose={onAlertPromptClosed}
        data-testid='blackbook-push-notification-prompt'
      />
    </Tray>,
    document.body
  )
}

function trackEventWithSource(
  eventType: 'push' | 'sms' | 'closed',
  data: Record<string, unknown> = {}
) {
  trackBlackbookAlertEvent(eventType, { source: 'BlackbookAlertPromptTray', ...data })
  incrementQuickCounter(`blackbook-tray-outcome-${eventType}`)
}
